// api
import { IAdserverCampaign, transformCampaign } from '~/src/dash/api/campaign/campaign';
import { ICompany, transformCompanyFromAPI, transformCompanyToAPI } from '~/src/dash/api/company';
import {
  EAllowStatus,
  IAdserverAlias,
  IUser,
  ResourceActive,
  transformUser,
} from '~/src/dash/api/console-api';
import {
  parseArray,
  parseNumber,
  parseObject,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

// |------------------ INTERFACES and TYPES ------------------|

export type AdserverOrderFrequencyCapActive = number;
export const AdserverOrderFrequencyCapActiveUnknown: AdserverOrderFrequencyCapActive = 0;
export const AdserverOrderFrequencyCapActiveInactive: AdserverOrderFrequencyCapActive = 1;
export const AdserverOrderFrequencyCapActiveActive: AdserverOrderFrequencyCapActive = 2;

export type AdserverOrderFrequencyCapTargetingKey = number;
export const AdserverOrderFrequencyCapTargetingKeyUnknown: AdserverOrderFrequencyCapTargetingKey = 0;
export const AdserverOrderFrequencyCapTargetingKeyImpression: AdserverOrderFrequencyCapTargetingKey = 1;
export const AdserverOrderFrequencyCapTargetingKeyDuration: AdserverOrderFrequencyCapTargetingKey = 2;

export interface IAdserverOrderFrequencyCap {
  ID: number;
  CreatedAt: Date;
  UpdatedAt: Date;
  DeletedAt: Date | null;
  Active: AdserverOrderFrequencyCapActive;
  AdserverOrderID: number;
  Limit: number;
  TimeToLive: number;
  TargetingKey: AdserverOrderFrequencyCapTargetingKey;
}

export interface IAdserverOrder {
  ID: number;
  CreatedAt: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Active: ResourceActive;
  AdserverOrderFrequencyCaps: IAdserverOrderFrequencyCap[];
  AgencyID?: number;
  Agency?: ICompany;
  AdvertiserID: number;
  Advertiser?: ICompany;
  Alias: string;
  Dayparting: string;
  DaypartingIsAllowed: EAllowStatus;
  Labels: string;
  LineItems: IAdserverCampaign[];
  Name: string;
  PacingType: number;
  PublisherID: number;
  PurchaseOrder: string;
  SalespersonID?: number;
  Salesperson?: IUser;
  TraffickerID: number;
  Trafficker?: IUser;
}

// |------------------ TRANSFORM FUNCTIONS ------------------|

export const transformAdserverOrderFrequencyCap = transformModel<IAdserverOrderFrequencyCap>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  Active: parseNumber(['Active']),
  AdserverOrderID: parseNumber(['AdserverOrderID']),
  Limit: parseNumber(['Limit']),
  TimeToLive: parseNumber(['TimeToLive']),
  TargetingKey: parseNumber(['TargetingKey']),
});

export const transformAdserverOrderFromAPI = transformModel<IAdserverOrder>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Active: parseNumber(['Active']),
  AdserverOrderFrequencyCaps: parseArray(
    ['AdserverOrderFrequencyCaps'],
    transformAdserverOrderFrequencyCap,
  ),
  AgencyID: parseNumber(['AgencyID']),
  Agency: parseObject(['Agency'], transformCompanyFromAPI),
  AdvertiserID: parseNumber(['AdvertiserID']),
  Advertiser: parseObject(['Advertiser'], transformCompanyFromAPI),
  Alias: parseString(['Alias']),
  Dayparting: parseString(['Dayparting']),
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  Labels: parseString(['Labels']),
  LineItems: parseArray(['LineItems'], transformCampaign),
  Name: parseString(['Name']),
  PacingType: parseNumber(['PacingType']),
  PublisherID: parseNumber(['PublisherID']),
  PurchaseOrder: parseString(['PurchaseOrder']),
  SalespersonID: parseNumber(['SalespersonID']),
  Salesperson: parseObject(['Salesperson'], transformUser),
  TraffickerID: parseNumber(['TraffickerID']),
  Trafficker: parseObject(['Trafficker'], transformUser),
});

// removed the dates
export const transformAdserverOrderToAPI = transformModel<IAdserverOrder>({
  ID: parseNumber(['ID']),

  Active: parseNumber(['Active']),
  AdserverOrderFrequencyCaps: parseArray(
    ['AdserverOrderFrequencyCaps'],
    transformAdserverOrderFrequencyCap,
  ),
  AgencyID: parseNumber(['AgencyID']),
  Agency: parseObject(['Agency'], transformCompanyToAPI),
  AdvertiserID: parseNumber(['AdvertiserID']),
  Advertiser: parseObject(['Advertiser'], transformCompanyToAPI),
  Alias: parseString(['Alias']),
  Dayparting: parseString(['Dayparting']),
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  Labels: parseString(['Labels']),
  LineItems: parseArray(['LineItems'], transformCampaign),
  Name: parseString(['Name']),
  PacingType: parseNumber(['PacingType']),
  PublisherID: parseNumber(['PublisherID']),
  PurchaseOrder: parseString(['PurchaseOrder']),
  SalespersonID: parseNumber(['SalespersonID']),
  Salesperson: parseObject(['Salesperson'], transformUser),
  TraffickerID: parseNumber(['TraffickerID']),
  Trafficker: parseObject(['Trafficker'], transformUser),
});

// |------------------ API CALLS ------------------|

export async function getAdserverOrder(
  orderID: string,
  publisherID: string,
  noPreload?: boolean,
): Promise<IAdserverOrder> {
  let reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/orders/${orderID}`);
  if (noPreload) {
    reqUrl = reqUrl + '&no_preload=true';
  }
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    return transformAdserverOrderFromAPI(val.parsedBody);
  });
}

export async function getAdserverOrders(
  publisherID: string,
  noPreload?: boolean,
): Promise<IAdserverOrder[]> {
  let reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/orders`);
  if (noPreload) {
    reqUrl = reqUrl + '&no_preload=true';
  }
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const AdserverOrders = [] as IAdserverOrder[];
    if (val.parsedBody) {
      for (const item of val.parsedBody) {
        const AdserverOrder = transformAdserverOrderFromAPI(item);
        AdserverOrders.push(AdserverOrder);
      }
    }
    return AdserverOrders;
  });
}

export async function getUniqueOrderAliases(publisherID: string): Promise<IAdserverAlias[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/orders_unique_aliases`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const uniqueOrderAliases = [];
    if (val.parsedBody) {
      for (const item of val.parsedBody) {
        uniqueOrderAliases.push({ Name: item });
      }
    }
    return uniqueOrderAliases;
  });
}

export async function createAdserverOrder(
  adserverOrder: IAdserverOrder,
  publisherID: string,
): Promise<IAdserverOrder> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/orders`);
  const parsedAdserverOrder = transformAdserverOrderToAPI(adserverOrder);
  return await restPost<IAdserverOrder>(reqUrl, parsedAdserverOrder).then(
    (val: IHttpResponse<any>) => {
      return transformAdserverOrderFromAPI(val.parsedBody);
    },
  );
}

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateAdserverOrder(
  adserverOrder: IAdserverOrder,
  publisherID: string,
  fullUpdate: boolean,
  makeAllChildrenSameActive: boolean,
): Promise<IAdserverOrder> {
  const partialUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/orders/${adserverOrder.ID}`,
  );
  let reqUrl = fullUpdate ? partialUrl + '&full_update=true' : partialUrl;
  reqUrl = makeAllChildrenSameActive ? partialUrl + '&make_children_active=true' : partialUrl;
  const parsedAdserverOrder = transformAdserverOrderToAPI(adserverOrder);
  return await restPatch<IAdserverOrder>(
    reqUrl,
    fullUpdate ? parsedAdserverOrder : adserverOrder,
  ).then((val: IHttpResponse<any>) => {
    return transformAdserverOrderFromAPI(val.parsedBody);
  });
}

export async function cloneAdserverOrder(
  adserverOrder: IAdserverOrder,
  publisherID: string,
  includeCreatives: boolean,
): Promise<IAdserverOrder> {
  const partialUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/orders/${adserverOrder.ID}/clone`,
  );
  const reqUrl = includeCreatives ? partialUrl + '&include_creatives=true' : partialUrl;
  const parsedAdserverOrder = transformAdserverOrderToAPI(adserverOrder);
  return await restPost<IAdserverOrder>(reqUrl, parsedAdserverOrder).then(
    (val: IHttpResponse<any>) => {
      return transformAdserverOrderFromAPI(val.parsedBody);
    },
  );
}
export async function deleteAdserverOrder(
  adserverOrderID: number,
  publisherID: string,
): Promise<IHttpResponse<IAdserverOrder>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/orders/${adserverOrderID}`);
  return await restDelete<IAdserverOrder>(reqUrl, undefined);
}
